import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Send, Sparkles, X } from "lucide-react";
import type { Teammate } from "@/lib/types";

type Props = { teammate: Teammate };

const SAMPLE_INPUTS: Record<string, string> = {
  sales:
    "Meeting notes: Acme Corp (mid-market retailer, 12 stores) — Head of Ops is frustrated with current vendor's turnaround (5 days avg). Interested in our 24h SLA but worried about migration downtime. Budget: ~45k/yr. Next step: technical deep-dive next week.",
  marketing:
    "Campaign goal: launch our new sustainability report to enterprise prospects. Audience: CMOs at 500-5000 employee companies. Past campaigns show video + case study combo converts 2.3x better than email-only.",
  finance:
    "Q3 OPEX variance: Marketing up 18% vs budget (extra campaign push), Travel down 12% (fewer conferences), Software licenses up 7% (Slack seat growth). Need a summary for the CFO by Friday.",
  operations:
    "Warehouse throughput dropped 14% last week. Fulfillment errors up. Two new team members started Monday. Receiving dock has had cart shortages on Tue and Thu. Need root cause analysis for the weekly stand-up.",
  research:
    "Literature snapshot: 12 recent papers on mRNA delivery to dendritic cells. Top 3 findings: improved lipid nanoparticle formulations, enhanced cell-specific targeting, reduced off-target inflammation.",
  hr:
    "Performance review draft for a senior engineer: consistently ships high-quality work, mentors juniors, but has raised concerns about team collaboration and direct communication style.",
  legal:
    "Contract review: SaaS agreement with a vendor. Key clauses: auto-renewal with 90-day notice, liability cap at 12 months fees, data processing addendum missing a sub-processor list.",
  admin:
    "Next Monday's exec offsite: 8 attendees, dietary restrictions (1 vegan, 2 gluten-free), flights arriving between 9-11am, dinner reservation needed for 7pm. Agenda doc still missing from 2 attendees.",
  other:
    "Weekly team update I need to draft: 3 projects shipped, 1 blocker with the infra team, hiring a new designer (interviews start Thu), team offsite next month.",
};

export function TryItNow({ teammate }: Props) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [running, setRunning] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const sample =
    (teammate.sampleInput && teammate.sampleInput.trim()) ||
    SAMPLE_INPUTS[teammate.function] ||
    SAMPLE_INPUTS.other;

  async function run() {
    if (!input.trim() || running) return;
    setRunning(true);
    setOutput("");
    const ac = new AbortController();
    abortRef.current = ac;

    try {
      const r = await fetch("/api/run", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ teammateId: teammate.id, input }),
        signal: ac.signal,
      });
      if (!r.ok || !r.body) {
        const text = await r.text();
        throw new Error(text.slice(0, 200));
      }
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        setOutput((o) => o + dec.decode(value, { stream: true }));
      }
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === "AbortError") return;
      setOutput((o) => o + `\n\n[error: ${e instanceof Error ? e.message : "run failed"}]`);
    } finally {
      setRunning(false);
    }
  }

  function stop() {
    abortRef.current?.abort();
    setRunning(false);
  }

  function loadSample() {
    setInput(sample);
  }

  return (
    <div className="glass glow-border mt-5 rounded-lg p-5">
      <div className="flex items-center gap-3">
        <div
          className="flex h-10 w-10 items-center justify-center rounded-xl"
          style={{ background: `${teammate.accent}22` }}
        >
          <Sparkles className="h-5 w-5 text-cyan-glow" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-cyan-glow">
            See it work
          </div>
          <div className="text-base font-medium text-white">
            Put {teammate.name} to work on a real input
          </div>
        </div>
        {!open && (
          <button className="btn-ghost" onClick={() => setOpen(true)}>
            Try now
          </button>
        )}
        {open && (
          <button
            onClick={() => {
              setOpen(false);
              setInput("");
              setOutput("");
              stop();
            }}
            className="text-white/50 hover:text-white"
            aria-label="close"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="mt-4 flex items-center justify-between">
              <div className="text-xs text-white/50">Paste your real task input</div>
              {!input && (
                <button
                  className="text-xs text-cyan-glow hover:underline"
                  onClick={loadSample}
                >
                  use a sample
                </button>
              )}
            </div>
            <textarea
              className="input-glass mt-2 min-h-[110px] resize-none text-sm"
              placeholder={`e.g. "${sample.slice(0, 100)}…"`}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              rows={4}
            />
            <div className="mt-2 flex justify-end">
              {running ? (
                <button className="btn-ghost" onClick={stop}>
                  Stop
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={run}
                  disabled={!input.trim()}
                >
                  Run {teammate.name} <Send className="h-4 w-4" />
                </button>
              )}
            </div>

            <AnimatePresence>
              {(output || running) && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="mt-4 rounded-lg p-4"
                  style={{
                    background: `linear-gradient(180deg, ${teammate.accent}14, ${teammate.accent}06)`,
                    border: `1px solid ${teammate.accent}33`,
                  }}
                >
                  <div
                    className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest"
                    style={{ color: teammate.accent }}
                  >
                    {teammate.name} says
                    {running && (
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-glow/80" />
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan-glow" />
                      </span>
                    )}
                  </div>
                  <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-white/90">
                    {output}
                    {running && <span className="animate-pulse text-cyan-glow">▍</span>}
                  </pre>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
